import { Box3, Plane, Vector3 } from 'three';

import { getBoundingBoxDiagonal } from '../geom-utils';

/** userData key on materials created by materials.ts cache (clone per mesh when clipping). */
export const PARTVIEWER_SHARED_MATERIAL_POOL = 'partViewerSharedMaterialPool';

export type CrossSectionNormalInput =
  | Vector3
  | { x: number; y: number; z: number }
  | undefined;

/** Corners of axis-aligned box for dot(min/max) range. */
const cornersTmp: Vector3[] = Array.from({ length: 8 }, () => new Vector3());

/** Padding on the quad that covers the AABB projected onto the clipping plane. */
const CROSS_SECTION_PLANE_QUAD_PADDING = 1.1;
/** Minimum quad span (fraction of AABB diagonal) when the silhouette is very thin. */
const CROSS_SECTION_PLANE_QUAD_MIN_SPAN = 0.03;

/**
 * Extend the clip slider slightly **past** the AABB along the clip axis (t=0 / t=1 sit outside
 * the shell), so “full clip” / “full view” ends clear the mesh and caps with less z-fighting.
 * Scales with bbox diagonal; capped vs projection span.
 */
const CROSS_SECTION_CLIP_END_OUTSET_DIAGONAL_FRAC = 2e-4;
const CROSS_SECTION_CLIP_END_OUTSET_MIN_ABS = 1e-7;
const CROSS_SECTION_CLIP_END_OUTSET_MAX_SPAN_FRAC = 0.08;

const planeQuadScratch = {
  u: new Vector3(),
  v: new Vector3(),
  helper: new Vector3(),
  ref: new Vector3(),
  proj: new Vector3(),
  boxCenter: new Vector3(),
};

function buildOrthonormalBasisInPlane(
  planeNormal: Vector3,
  uOut: Vector3,
  vOut: Vector3,
): void {
  const { helper } = planeQuadScratch;
  helper.set(1, 0, 0);
  if (Math.abs(planeNormal.dot(helper)) > 0.9) {
    helper.set(0, 1, 0);
  }
  uOut.crossVectors(planeNormal, helper).normalize();
  vOut.crossVectors(planeNormal, uOut);
}

/**
 * World-space center and full width/height for a quad on `plane` that wraps the projection of
 * the **world AABB** corners (same box as clipping uses). `width` is along `u`, `height` along `v`.
 * When `outU` / `outV` are passed, they receive that same orthonormal basis so the quad mesh can
 * be oriented with `makeBasis(outU, outV, …)` instead of `lookAt` (which would misalign local X/Y).
 */
export function computeCrossSectionPlaneQuadFromAabb(
  box: Box3,
  plane: Plane,
  outCenter: Vector3,
  outU?: Vector3,
  outV?: Vector3,
): { width: number; height: number } {
  const { u, v, ref, proj, boxCenter } = planeQuadScratch;
  buildOrthonormalBasisInPlane(plane.normal, u, v);

  box.getCenter(boxCenter);
  plane.projectPoint(boxCenter, ref);

  fillBoxCorners(box, cornersTmp);
  const ru = u.dot(ref);
  let minS = Infinity;
  let maxS = -Infinity;
  let minT = Infinity;
  let maxT = -Infinity;
  for (let i = 0; i < 8; i++) {
    plane.projectPoint(cornersTmp[i], proj);
    const s = u.dot(proj) - ru;
    const t = v.dot(proj) - v.dot(ref);
    minS = Math.min(minS, s);
    maxS = Math.max(maxS, s);
    minT = Math.min(minT, t);
    maxT = Math.max(maxT, t);
  }

  const diag = getBoundingBoxDiagonal(box);
  const minSpan = Math.max(diag * CROSS_SECTION_PLANE_QUAD_MIN_SPAN, 1e-9);
  const width = Math.max(
    (maxS - minS) * CROSS_SECTION_PLANE_QUAD_PADDING,
    minSpan,
  );
  const height = Math.max(
    (maxT - minT) * CROSS_SECTION_PLANE_QUAD_PADDING,
    minSpan,
  );

  const midS = 0.5 * (minS + maxS);
  const midT = 0.5 * (minT + maxT);
  outCenter.copy(ref).addScaledVector(u, midS).addScaledVector(v, midT);

  if (outU) outU.copy(u);
  if (outV) outV.copy(v);

  return { width, height };
}

function fillBoxCorners(box: Box3, corners: Vector3[]): void {
  const { min, max } = box;
  let i = 0;
  for (const x of [min.x, max.x]) {
    for (const y of [min.y, max.y]) {
      for (const z of [min.z, max.z]) {
        corners[i++].set(x, y, z);
      }
    }
  }
}

/** World-space unit normal along the longest AABB edge direction. */
export function getLongestAabbAxisNormal(box: Box3): Vector3 {
  const dx = box.max.x - box.min.x;
  const dy = box.max.y - box.min.y;
  const dz = box.max.z - box.min.z;
  if (dx >= dy && dx >= dz) return new Vector3(1, 0, 0);
  if (dy >= dz) return new Vector3(0, 1, 0);
  return new Vector3(0, 0, 1);
}

/**
 * If `normalIn` is non-degenerate, returns it normalized. Otherwise −Z (view cube "Bottom").
 */
export function resolveCrossSectionNormal(
  normalIn: CrossSectionNormalInput,
): Vector3 {
  const n = new Vector3(
    normalIn && 'x' in normalIn ? normalIn.x : 0,
    normalIn && 'y' in normalIn ? normalIn.y : 0,
    normalIn && 'z' in normalIn ? normalIn.z : 0,
  );
  if (n.lengthSq() > 1e-12) {
    return n.normalize();
  }
  // Cube "Bottom" face is −Z (see cube-utils `baseFaces`); use that when normal is unset.
  return new Vector3(0, 0, -1);
}

type CrossSectionProjectionExtents = {
  /** Unit section axis (clip / slider direction). */
  n: Vector3;
  kMin: number;
  kMax: number;
  span: number;
  /** Same margin as slider end outsets (`kLo` / `kHi` past hull). */
  margin: number;
  kLo: number;
  kHi: number;
};

function crossSectionProjectionExtents(
  unitNormal: Vector3,
  aabb: Box3,
): CrossSectionProjectionExtents {
  const n = unitNormal.clone().normalize();
  fillBoxCorners(aabb, cornersTmp);
  let kMin = Infinity;
  let kMax = -Infinity;
  for (let i = 0; i < 8; i++) {
    const d = n.dot(cornersTmp[i]);
    kMin = Math.min(kMin, d);
    kMax = Math.max(kMax, d);
  }
  const span = kMax - kMin;
  let margin = 0;
  if (span > 0 && Number.isFinite(span)) {
    const diag = getBoundingBoxDiagonal(aabb);
    const desired = Math.max(
      diag * CROSS_SECTION_CLIP_END_OUTSET_DIAGONAL_FRAC,
      CROSS_SECTION_CLIP_END_OUTSET_MIN_ABS,
    );
    margin = Math.min(
      desired,
      span * CROSS_SECTION_CLIP_END_OUTSET_MAX_SPAN_FRAC,
    );
  }
  return {
    n,
    kMin,
    kMax,
    span,
    margin,
    kLo: kMin - margin,
    kHi: kMax + margin,
  };
}

/**
 * World-units margin by which the cross-section slider extends past the projected AABB along the
 * clip axis — identical to the outset used at slider ends for caps / z-fighting mitigation.
 */
export function getCrossSectionEndcapMargin(
  unitNormal: Vector3,
  aabb: Box3,
): number {
  return crossSectionProjectionExtents(unitNormal, aabb).margin;
}

/**
 * Updates `plane` so the cut sweeps through `aabb` as `position` goes 0→1.
 * `position` is clamped to [0, 1]. Slider endpoints map slightly **beyond** the projected AABB
 * min/max along **unitNormal** (section direction from Redux), so full clip / full view clears
 * the mesh shells and section caps (less z-fighting than ending exactly on the hull).
 *
 * `inverted` only flips which side of the **same** world plane is clipped (clip normal negated);
 * the plane position along `unitNormal` does not change when toggling invert.
 */
export function updateCrossSectionPlaneFromSlider(
  plane: Plane,
  unitNormal: Vector3,
  aabb: Box3,
  position: number,
  inverted: boolean,
): void {
  const ext = crossSectionProjectionExtents(unitNormal, aabb);
  const t = Math.min(1, Math.max(0, position));
  let k: number;
  if (!(ext.span > 0) || !Number.isFinite(ext.span)) {
    k = ext.kMin + t * ext.span;
  } else {
    k = ext.kLo + t * (ext.kHi - ext.kLo);
  }
  const coplanarPoint = ext.n.clone().multiplyScalar(k);
  const nClip = inverted ? ext.n.clone().negate() : ext.n;
  plane.setFromNormalAndCoplanarPoint(nClip, coplanarPoint);
}

/**
 * Slider `position` in [0, 1] so {@link updateCrossSectionPlaneFromSlider} with the same
 * `unitNormal`, `aabb`, and that `position` places the cut through the picked face depth
 * (projection of `worldPoint` onto the section axis), matching the slider range including end
 * outsets. Invert does not affect this mapping — it only flips the clipped half-space.
 */
export function crossSectionSliderPositionForWorldPoint(
  unitNormal: Vector3,
  aabb: Box3,
  worldPoint: Vector3,
): number {
  const ext = crossSectionProjectionExtents(unitNormal, aabb);
  if (!(ext.span > 0) || !Number.isFinite(ext.span)) {
    return 0.5;
  }
  const denom = ext.kHi - ext.kLo;
  if (!(denom > 0) || !Number.isFinite(denom)) {
    return 0.5;
  }
  const s = ext.n.dot(worldPoint);
  const t = (s - ext.kLo) / denom;
  return Math.min(1, Math.max(0, t));
}

/** Unit normals for "Change axis" cycling (world space). */
export const WORLD_CROSS_SECTION_AXES: ReadonlyArray<{
  x: number;
  y: number;
  z: number;
}> = [
  { x: 1, y: 0, z: 0 },
  { x: -1, y: 0, z: 0 },
  { x: 0, y: 1, z: 0 },
  { x: 0, y: -1, z: 0 },
  { x: 0, y: 0, z: 1 },
  { x: 0, y: 0, z: -1 },
];

/** Advance to the next preset world axis (for toolbar "Change axis"). */
export function cycleWorldCrossSectionNormal(
  current: { x: number; y: number; z: number } | undefined,
): { x: number; y: number; z: number } {
  const list = WORLD_CROSS_SECTION_AXES;
  const cx = current?.x ?? 0;
  const cy = current?.y ?? 0;
  const cz = current?.z ?? 0;
  const len = Math.hypot(cx, cy, cz);
  let bestI = 0;
  let bestAbsDot = -1;
  if (len > 1e-9) {
    for (let i = 0; i < list.length; i++) {
      const ax = list[i];
      const dot = (cx * ax.x + cy * ax.y + cz * ax.z) / len;
      const ad = Math.abs(dot);
      if (ad > bestAbsDot) {
        bestAbsDot = ad;
        bestI = i;
      }
    }
  }
  return list[(bestI + 1) % list.length];
}
